import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAdminFilters } from "@/contexts/AdminFiltersContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Activity, ChevronDown, ChevronLeft, ChevronRight, UserPlus, Pencil,
  Trash2, Eye, FolderPlus, FolderOpen, Download, Upload, Shield, LogIn,
  Key, CreditCard, UserCheck, Search, X, Calendar, Send, ArrowRight, Loader2,
  AtSign, Mail, Clock, Forward, Ban, Check, Square, CheckSquare, MessageSquare,
  Paperclip, XCircle, Inbox, RefreshCw,
} from "lucide-react";

// ── Actions tab config ──
const EVENT_ICONS = {
  contact_created: UserPlus, contact_updated: Pencil, contact_deleted: Trash2,
  contacts_batch_deleted: Trash2, visibility_changed: Eye,
  group_created: FolderPlus, group_updated: FolderOpen, group_deleted: Trash2,
  import_committed: Upload, contacts_exported: Download,
  admin_login_success: LogIn, admin_login_failed: Shield,
  admin_2fa_enabled: Key, admin_2fa_setup_started: Key,
  admin_recovery_code_used: Key, admin_user_role_changed: Shield,
  admin_user_disabled: Shield, admin_user_enabled: Shield,
  admin_viewed_activity_logs: Activity, admin_access_denied_no_2fa: Shield,
};
const EVENT_COLORS = {
  admin_login_success: "text-green-600 bg-green-50",
  admin_login_failed: "text-red-600 bg-red-50",
  admin_2fa_enabled: "text-blue-600 bg-blue-50",
  admin_recovery_code_used: "text-amber-600 bg-amber-50",
  admin_user_role_changed: "text-violet-600 bg-violet-50",
  admin_user_disabled: "text-red-500 bg-red-50",
  admin_user_enabled: "text-green-600 bg-green-50",
  admin_access_denied_no_2fa: "text-red-600 bg-red-50",
  contact_created: "text-emerald-600 bg-emerald-50",
  contact_updated: "text-blue-600 bg-blue-50",
  contact_deleted: "text-red-500 bg-red-50",
  contacts_batch_deleted: "text-red-500 bg-red-50",
  visibility_changed: "text-violet-600 bg-violet-50",
  group_created: "text-teal-600 bg-teal-50",
  import_committed: "text-amber-600 bg-amber-50",
  contacts_exported: "text-[#888888] bg-[#F5F5F5]",
};
const ENTITY_FILTERS = [
  { key: "", label: "All" }, { key: "system", label: "Security" },
  { key: "contact", label: "Contacts" }, { key: "group", label: "Groups" },
  { key: "import", label: "Imports" },
];

// ── Lifecycle tab config (v2 structured schema) ──
const CATEGORY_ICONS = {
  auth: LogIn, email: Send, alias: Key, plan: CreditCard, admin: Eye, system: Shield,
};
const CATEGORY_COLORS = {
  auth: "text-green-600 bg-green-50",
  email: "text-blue-600 bg-blue-50",
  alias: "text-cyan-600 bg-cyan-50",
  plan: "text-violet-600 bg-violet-50",
  admin: "text-amber-600 bg-amber-50",
  system: "text-[#888888] bg-[#F5F5F5]",
};
const ACTION_COLORS = {
  login: "text-green-600", registered: "text-emerald-600", created: "text-blue-600",
  deleted: "text-red-600", archived: "text-amber-600", sent: "text-emerald-600",
  received: "text-blue-600", received_email: "text-blue-600",
  impersonated: "text-amber-600", impersonation_sent: "text-amber-600",
  failed: "text-red-600", plan_changed: "text-violet-600",
  claimed: "text-cyan-600", activated: "text-emerald-600", canceled: "text-red-600",
};
const STATUS_STYLES = {
  success: "bg-emerald-50 text-emerald-600 border-emerald-200",
  failed: "bg-red-50 text-red-600 border-red-200",
  blocked: "bg-red-50 text-red-700 border-red-300",
  denied: "bg-red-50 text-red-700 border-red-300",
  pending: "bg-amber-50 text-amber-600 border-amber-200",
  verified: "bg-blue-50 text-blue-600 border-blue-200",
};
const LIFECYCLE_FILTERS = [
  { key: "", label: "All" },
  { key: "auth", label: "Auth", isCategory: true },
  { key: "alias", label: "Aliases", isCategory: true },
  { key: "email", label: "Emails", isCategory: true },
  { key: "plan", label: "Plans", isCategory: true },
  { key: "admin", label: "Admin", isCategory: true },
  { key: "__blocked__", label: "Blocked", isStatus: true, tone: "danger" },
  // Phase 4: canonical v3 event buckets — collapse matching legacy actions.
  // Uses `canonical_event_type` query param (backend reverse-maps to all aliases).
  { key: "user_login", label: "User login", isCanonical: true },
  { key: "user_logout", label: "User logout", isCanonical: true },
  { key: "user_login_failed", label: "User login failed", isCanonical: true, tone: "warn" },
  { key: "account_upgraded_to_pro", label: "Upgraded to Pro", isCanonical: true },
  { key: "payment_failed", label: "Payment failed", isCanonical: true, tone: "danger" },
  { key: "login_challenge_required", label: "Login challenge", isCanonical: true, tone: "warn" },
  { key: "alias_receive_blocked", label: "Alias receive blocked", isCanonical: true, tone: "danger" },
  { key: "system_error", label: "System error", isCanonical: true, tone: "danger" },
];

// Human-friendly label map for canonical v3 event_types.
// Used in row rendering when `canonical_event_type` is present on a log entry.
const CANONICAL_HUMAN_LABELS = {
  user_registered: "User registered",
  user_login: "User login",
  user_login_failed: "User login failed",
  user_logout: "User logout",
  user_password_changed: "Password changed",
  alias_created: "Alias created",
  alias_deleted: "Alias deleted",
  alias_claimed: "Alias claimed",
  alias_reactivated: "Alias reactivated",
  alias_accessed: "Alias accessed",
  alias_send_blocked: "Alias send blocked",
  alias_receive_blocked: "Alias receive blocked",
  email_stored: "Email stored",
  email_deleted: "Email deleted",
  email_send_failed: "Email send failed",
  attachment_uploaded: "Attachment uploaded",
  attachment_rejected: "Attachment rejected",
  attachment_downloaded: "Attachment downloaded",
  payment_success: "Payment success",
  payment_failed: "Payment failed",
  account_upgraded_to_pro: "Account upgraded to Pro",
  account_downgraded_to_free: "Account downgraded to free",
  subscription_cancelled: "Subscription cancelled",
  subscription_expired: "Subscription expired",
  subscription_renewed: "Subscription renewed",
  webhook_received: "Webhook received",
  webhook_failed: "Webhook failed",
  reputation_score_changed: "Reputation score changed",
  suspicious_activity_detected: "Suspicious activity detected",
  session_invalidated: "Session invalidated",
  access_denied: "Access denied",
  login_challenge_required: "Login challenge required",
  admin_user_viewed: "Admin viewed user",
  admin_role_changed: "Admin role changed",
  admin_user_deactivated: "Admin deactivated user",
  admin_user_reactivated: "Admin reactivated user",
  admin_force_logout: "Admin force logout",
  admin_alias_reactivated: "Admin reactivated alias",
  system_error: "System error",
};

// ── Date presets ──
function getDatePreset(key) {
  const now = new Date();
  const startOfDay = (d) => { const r = new Date(d); r.setHours(0, 0, 0, 0); return r; };
  const endOfDay = (d) => { const r = new Date(d); r.setHours(23, 59, 59, 999); return r; };
  const minsAgo = (m) => new Date(now.getTime() - m * 60 * 1000);
  switch (key) {
    case "5m": return { from: minsAgo(5).toISOString(), to: now.toISOString() };
    case "15m": return { from: minsAgo(15).toISOString(), to: now.toISOString() };
    case "1h": return { from: minsAgo(60).toISOString(), to: now.toISOString() };
    case "24h": return { from: minsAgo(60 * 24).toISOString(), to: now.toISOString() };
    case "today": return { from: startOfDay(now).toISOString(), to: endOfDay(now).toISOString() };
    case "yesterday": { const y = new Date(now); y.setDate(y.getDate() - 1); return { from: startOfDay(y).toISOString(), to: endOfDay(y).toISOString() }; }
    case "7d": { const d = new Date(now); d.setDate(d.getDate() - 7); return { from: startOfDay(d).toISOString(), to: endOfDay(now).toISOString() }; }
    case "30d": { const d = new Date(now); d.setDate(d.getDate() - 30); return { from: startOfDay(d).toISOString(), to: endOfDay(now).toISOString() }; }
    default: return { from: "", to: "" };
  }
}
const DATE_PRESETS = [
  { key: "5m", label: "5 min" },
  { key: "15m", label: "15 min" },
  { key: "1h", label: "1 hour" },
  { key: "24h", label: "24 hours" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "", label: "All time" },
  { key: "custom", label: "Custom" },
];

// ── Formatters ──
function formatEvent(log) {
  const md = log.metadata || {};
  const actor = log.actor_email || md.email || "";
  switch (log.event_type || log.action) {
    case "contact_created": return `${actor || "User"} created contact: ${md.email || ""}`;
    case "contact_updated": return `${actor || "User"} updated contact (${(md.changed_fields || []).join(", ")})`;
    case "contact_deleted": return `${actor || "User"} deleted contact`;
    case "contacts_batch_deleted": return `${actor || "User"} deleted ${md.count || 0} contacts`;
    case "visibility_changed": return `${actor || "User"} changed visibility to ${(md.new_visibility || "").replace(/_/g, " ")}`;
    case "group_created": return `${actor || "User"} created group: ${md.name || ""}`;
    case "group_updated": return `${actor || "User"} updated group`;
    case "group_deleted": return `${actor || "User"} deleted group`;
    case "import_committed": return `${actor || "User"} imported ${md.total || 0} contacts`;
    case "contacts_exported": return `${actor || "User"} exported ${md.result_count || 0} contacts`;
    case "admin_login_success": return `Admin login: ${md.email || ""} (${md.method || ""})`;
    case "admin_login_failed": return `Admin login failed: ${md.email || ""} — ${md.reason || ""}`;
    case "admin_2fa_enabled": return `${md.email || "Admin"} enabled 2FA`;
    case "admin_2fa_setup_started": return `${md.email || "Admin"} started 2FA setup`;
    case "admin_recovery_code_used": return `${md.email || "Admin"} used recovery code (${md.remaining_codes} left)`;
    case "admin_user_role_changed": return `${actor || "Admin"} changed role: ${md.target_email || ""} (${md.previous_value} \u2192 ${md.new_value})`;
    case "admin_user_disabled": return `${actor || "Admin"} disabled user: ${md.target_email || ""}`;
    case "admin_user_enabled": return `${actor || "Admin"} enabled user: ${md.target_email || ""}`;
    case "user_created": return `${actor || "Admin"} created user: ${md.email || ""} (${md.plan || "free"})`;
    case "user_impersonation_started": return `${actor || md.admin_email || "Admin"} impersonated: ${md.email || ""}`;
    case "impersonation_email_sent": return `${actor || "Admin"} sent email as ${md.target_email || ""} \u2192 ${md.to_email || ""}`;
    case "user_force_logout": return `${actor || "Admin"} force-logged out: ${md.email || ""}`;
    case "entitlement_edited": return `${actor || "Admin"} edited entitlement: ${md.username || ""}`;
    case "pro_account_blocked": return `${actor || "Admin"} blocked: ${md.username || ""} — ${md.reason || ""}`;
    case "pro_account_unblocked": return `${actor || "Admin"} unblocked: ${md.username || ""}`;
    case "search": return `${actor || "Admin"} searched: "${md.query || ""}" (${md.result_count || 0} results)`;
    case "query_executed": return `${actor || "Admin"} ran query: ${md.query_id || ""}`;
    default: return (log.event_type || log.action || "").replace(/[._]/g, " ");
  }
}
function formatLifecycle(log) {
  const ctx = log.context || {};
  const email = ctx.email || ctx.target_email || "";
  const plan = ctx.plan || "";
  const planLabel = plan === "yearly" ? "Pro Yearly" : plan === "pro" ? "Pro Monthly" : plan === "free" ? "Free" : plan;

  switch (log.event_type) {
    case "user.registered": {
      const pl = planLabel || "Free";
      return `${email} registered (${pl})`;
    }
    case "user.created": {
      const pl = planLabel ? ` (${planLabel})` : "";
      const by = ctx.source === "admin_manual" ? `Admin created` : "User created";
      return `${by}${pl}: ${email}`;
    }
    case "user.login": {
      const method = ctx.method || "password";
      if (ctx["2fa_pending"]) return `${email} — login (2FA pending)`;
      if (ctx["2fa_setup_required"]) return `${email} — login (2FA setup)`;
      if (method === "totp_2fa") return `${email} — login (2FA verified)`;
      return `${email} — login`;
    }
    case "plan_changed": {
      const prev = ctx.previous_plan || "?";
      const next = ctx.new_plan || "?";
      const prevLabel = prev === "yearly" ? "Pro Yearly" : prev === "pro" ? "Pro Monthly" : prev === "free" ? "Free" : prev;
      const nextLabel = next === "yearly" ? "Pro Yearly" : next === "pro" ? "Pro Monthly" : next === "free" ? "Free" : next;
      const src = ctx.source === "admin_manual" ? " (by admin)" : ctx.source === "admin_entitlement_editor" ? " (admin edit)" : ctx.source === "stripe_checkout" ? " (Stripe)" : "";
      return `${email || "User"}: ${prevLabel} \u2192 ${nextLabel}${src}`;
    }
    // Impersonation events use ImpersonationLabel component (handled separately in render)
    case "user_impersonation_started": {
      const admin = ctx.admin_email || "admin";
      const target = ctx.target_email || email;
      return `${admin} impersonated ${target}`;
    }
    case "impersonation_email_sent": {
      const admin = ctx.admin_email || "admin";
      const target = ctx.target_email || email;
      const to = ctx.to_email || "";
      return `${admin} sent as ${target} \u2192 ${to}`;
    }
    case "subscription.activated": return `${email || "User"}: subscription activated (${ctx.plan_id || ""} ${ctx.interval || ""})`;
    case "subscription.canceled": return `${email || "User"}: subscription canceled`;
    case "subscription.reactivated": return `${email || "User"}: subscription reactivated`;

    // Alias events
    case "alias.created": return `Alias created: ${ctx.address || ctx.alias_id || ""}`;
    case "alias.deleted": return `Alias deleted: ${ctx.alias_id || ""}`;
    case "free_alias.created": return `Free alias created: ${ctx.alias_id || ""}`;
    case "free_alias.claimed": return `Free alias claimed: ${ctx.alias_id || ""}`;
    case "free_alias.auto_claimed": return `Free alias auto-claimed: ${ctx.alias_id || ""} (${ctx.emails_migrated || 0} emails migrated)`;
    case "free_inbox.authenticated": return `Free inbox accessed: ${ctx.alias_id || ""}`;

    // Email events
    case "email.received": return `Email received: "${ctx.subject || ""}" from ${ctx.sender || ""} \u2192 ${ctx.alias_id || ""}`;
    case "email.sent": return `Email sent: "${ctx.subject || ""}" \u2192 ${ctx.to || ""}${ctx.success === false ? " (failed)" : ""}`;
    case "email.archive": return `Email archived: ${ctx.email_id || ""}`;
    case "email.unarchive": return `Email unarchived: ${ctx.email_id || ""}`;
    case "email.delete": return `Email deleted: ${ctx.email_id || ""}`;
    case "email.read": return `Email marked read: ${ctx.email_id || ""}`;
    case "email.unread": return `Email marked unread: ${ctx.email_id || ""}`;
    case "email.vip": return `Email marked VIP: ${ctx.email_id || ""}`;
    case "email.unvip": return `Email unmarked VIP: ${ctx.email_id || ""}`;
    case "email.test_forward": return `Test forward: ${ctx.alias_id || ""} \u2192 ${ctx.forward_to || ""}${ctx.success === false ? " (failed)" : ""}`;

    // Claim events
    case "claim.failed": return `Claim failed: ${ctx.reason || ""}`;
    case "claim.rate_limited": return `Claim rate limited`;

    // Payment events
    case "payment.checkout_created": return `Checkout: ${ctx.plan_id || ""} ($${((ctx.amount || 0) / 100).toFixed(2)})`;
    case "subscription.checkout_created": return `Subscription checkout: ${ctx.plan_id || ""} (${ctx.interval || ""})`;

    default: return log.event_type.replace(/[._]/g, " ");
  }
}
function formatTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("de-DE", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

// ── Human-readable action labels (used in Action column for v2 events) ──
const ACTION_LABELS = {
  alias_reactivated: "reactivated alias",
  alias_extended: "extended alias TTL",
  alias_reactivation_blocked_expired_window: "reactivation blocked (window expired)",
  alias_reactivation_blocked_reassigned: "reactivation blocked (reassigned)",
  received_email: "received email",
  email_received: "received email",
  email_sent: "sent email",
  sent_email: "sent email",
  admin_login_failed: "admin login failed",
  admin_login_success: "admin login",
  admin_access_denied_no_2fa: "access denied (no 2FA)",
  user_impersonation_started: "started impersonation",
  impersonation_email_sent: "sent email via impersonation",
  login_failed: "login failed",
  user_created: "created user",
  user_role_changed: "changed role",
  user_disabled: "disabled user",
  user_enabled: "enabled user",
  pro_account_blocked: "blocked PRO account",
  pro_account_unblocked: "unblocked PRO account",
  entitlement_edited: "edited entitlement",
  plan_changed: "changed plan",
};

function humanActionLabel(action) {
  if (!action) return "";
  return ACTION_LABELS[action] || action.replace(/[._]/g, " ");
}

function fmtDetailDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleString("de-DE", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}


// ── Clickable impersonation label renderer ──
function ImpersonationLabel({ log, onImpersonate }) {
  const ctx = log.context || {};
  const isEmailSent = log.event_type === "impersonation_email_sent";
  const admin = ctx.admin_email || "admin";
  const target = ctx.target_email || "";
  const userId = log.user_id || "";
  const toEmail = ctx.to_email || "";
  const subject = ctx.subject || "";

  if (isEmailSent) {
    return (
      <span className="text-sm text-[#555555]">
        <span className="text-[#888888]">Impersonation:</span>{" "}
        <span className="font-mono text-[#555555]">{admin}</span>{" "}
        <span className="text-[#888888]">as</span>{" "}
        {userId ? (
          <button
            onClick={() => onImpersonate(userId, target)}
            className="font-mono text-amber-600 hover:text-amber-900 hover:underline underline-offset-2 transition-colors"
            title={`Impersonate ${target}`}
            data-testid={`impersonate-link-${userId}`}
          >{target}</button>
        ) : (
          <span className="font-mono text-[#555555]">{target}</span>
        )}
        {" "}<ArrowRight size={12} className="inline text-[#999999] mx-0.5" />{" "}
        <span className="font-mono text-blue-600">{toEmail}</span>
        {subject && (
          <span className="ml-1.5 text-xs text-[#888888]">"{subject.length > 40 ? subject.slice(0, 40) + "..." : subject}"</span>
        )}
      </span>
    );
  }

  // user_impersonation_started
  return (
    <span className="text-sm text-[#555555]">
      <span className="text-[#888888]">Impersonation:</span>{" "}
      <span className="font-mono text-[#555555]">{admin}</span>{" "}
      <span className="text-[#888888]">as</span>{" "}
      {userId ? (
        <button
          onClick={() => onImpersonate(userId, target)}
          className="font-mono text-amber-600 hover:text-amber-900 hover:underline underline-offset-2 transition-colors"
          title={`Impersonate ${target}`}
          data-testid={`impersonate-link-${userId}`}
        >{target}</button>
      ) : (
        <span className="font-mono text-[#555555]">{target}</span>
      )}
    </span>
  );
}


/* Highlight matching substring in text */
function HighlightText({ text, query }) {
  if (!query || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-100 text-inherit rounded-sm px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}


// ── Main Component ──
export default function AdminActivityPage() {
  const navigate = useNavigate();
  const { hideTestData } = useAdminFilters();
  const [tab, setTab] = useState("lifecycle");

  // ── User detail dialog state ──
  const [userDetail, setUserDetail] = useState(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  const openUserDetail = async (userId) => {
    if (!userId) return;
    setUserDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/detail`);
      setUserDetail(data);
    } catch {
      toast.error("Failed to load user details");
    } finally {
      setUserDetailLoading(false);
    }
  };

  const openAliasInbox = (aliasId) => {
    if (!aliasId) return;
    // Store alias selection and navigate to inbox
    try { localStorage.setItem("inbox_sidebar_selection", JSON.stringify({ aliasId, folder: "inbox" })); } catch {}
    navigate("/inbox");
  };

  // ── Shared filter state ──
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [datePreset, setDatePreset] = useState("15m");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // "desc" | "asc"

  // Phase 5.3 — Deep-link resolution: ?event_id=… prefills search and
  // scrolls/highlights the matching row when it appears in results.
  const [searchParams] = useSearchParams();
  const [highlightEventId, setHighlightEventId] = useState("");

  // ── Actions tab state ──
  const [actionLogs, setActionLogs] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionHasMore, setActionHasMore] = useState(false);
  const [actionCursor, setActionCursor] = useState(null);
  const [entityFilter, setEntityFilter] = useState("");

  // ── Lifecycle tab state ──
  const [lifecycleLogs, setLifecycleLogs] = useState([]);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [lifecycleTotal, setLifecycleTotal] = useState(0);
  const [lifecycleSkip, setLifecycleSkip] = useState(0);
  const [lifecycleFilter, setLifecycleFilter] = useState("");
  const [impersonatingId, setImpersonatingId] = useState(null);

  // ── Event selection / basket state ──
  const [selectedEvents, setSelectedEvents] = useState([]); // [{event_id, user_id, user_label, action, timestamp, display_text}]
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState("");
  const [selectedUserEmail, setSelectedUserEmail] = useState(""); // Full deliverable email
  const [showConflictPrompt, setShowConflictPrompt] = useState(null); // pending event to add after conflict
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const [composeRecipient, setComposeRecipient] = useState("");
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeAttachments, setComposeAttachments] = useState([]); // [{filename, content_b64, content_type, size}]
  const [sending, setSending] = useState(false);

  const resolveEventUserId = (log) => {
    const actor = log.actor || {};
    const meta = log.metadata || {};
    // label_short = display handle (e.g. tester@QIB), label = full email (e.g. tester@quickinbox.app)
    if (actor.type === "user" && actor.id) {
      return {
        userId: actor.id,
        label: actor.label_short || actor.label || actor.id.slice(0, 12),
        fullEmail: actor.label && actor.label.includes("@") ? actor.label : "",
      };
    }
    if (meta.user_id) {
      return {
        userId: meta.user_id,
        label: meta.user_name || meta.user_id.slice(0, 12),
        fullEmail: meta.user_email || "",
      };
    }
    return { userId: actor.id || "", label: actor.label_short || actor.label || "unknown", fullEmail: actor.label && actor.label.includes("@") ? actor.label : "" };
  };

  const toggleEventSelection = (log, index) => {
    const eventId = log.event_type ? `${log.event_type}_${log.timestamp}_${index}` : `${log.action}_${log.timestamp}_${index}`;
    const isAlreadySelected = selectedEvents.some(e => e.event_id === eventId);

    if (isAlreadySelected) {
      const next = selectedEvents.filter(e => e.event_id !== eventId);
      setSelectedEvents(next);
      if (next.length === 0) { setSelectedUserId(null); setSelectedUserLabel(""); }
      return;
    }

    const { userId, label, fullEmail } = resolveEventUserId(log);
    const displayAction = (log.action || log.event_type || "").replace(/_/g, " ");
    const displayTarget = (log.target || {}).label_short || (log.target || {}).label || "";
    const displayText = displayTarget ? `${displayAction} — ${displayTarget}` : displayAction;

    const newEvent = {
      event_id: eventId,
      user_id: userId,
      user_label: label,
      user_email: fullEmail,
      action: displayAction,
      timestamp: log.timestamp,
      display_text: displayText,
    };

    // Check single-user constraint
    if (selectedUserId && userId && userId !== selectedUserId) {
      setShowConflictPrompt(newEvent);
      return;
    }

    setSelectedEvents(prev => [...prev, newEvent]);
    if (!selectedUserId && userId) {
      setSelectedUserId(userId);
      setSelectedUserLabel(label);
      setSelectedUserEmail(fullEmail);
    }
  };

  const handleConflictReset = () => {
    if (!showConflictPrompt) return;
    const ev = showConflictPrompt;
    setSelectedEvents([ev]);
    setSelectedUserId(ev.user_id);
    setSelectedUserLabel(ev.user_label);
    setSelectedUserEmail(ev.user_email || "");
    setShowConflictPrompt(null);
  };

  const clearSelection = () => {
    setSelectedEvents([]);
    setSelectedUserId(null);
    setSelectedUserLabel("");
    setSelectedUserEmail("");
  };

  const buildMessageBody = () => {
    const lines = selectedEvents.map(e => {
      const ts = e.timestamp ? formatTime(e.timestamp) : "";
      return `- ${ts}: ${e.display_text}`;
    });
    return `We noticed the following activities on your account:\n\n${lines.join("\n")}\n\nPlease review and let us know if you have any questions.`;
  };

  const handleQuickCopy = () => {
    const body = buildMessageBody();
    navigator.clipboard.writeText(body).then(() => {
      toast.success("Message copied to clipboard");
      clearSelection();
    }).catch(() => toast.error("Copy failed"));
  };

  const openEmailCompose = () => {
    // Use full deliverable email for To, fall back to label if no email found
    const toEmail = selectedUserEmail || selectedUserLabel || selectedUserId || "";
    setComposeRecipient(toEmail);
    setComposeSubject("Account activity notice");
    setComposeBody(buildMessageBody());
    setComposeAttachments([]);
    setEmailComposeOpen(true);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name} exceeds 5MB limit`); return; }
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = reader.result.split(",")[1];
        setComposeAttachments(prev => [...prev, {
          filename: file.name,
          content_b64: b64,
          content_type: file.type || "application/octet-stream",
          size: file.size,
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const handleSend = async () => {
    if (!composeRecipient.trim() || !composeSubject.trim() || !composeBody.trim()) {
      toast.error("To, Subject, and Body are required");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/send-email", {
        to: composeRecipient,
        subject: composeSubject,
        body: composeBody,
        attachments: composeAttachments.map(a => ({
          filename: a.filename,
          content_b64: a.content_b64,
          content_type: a.content_type,
        })),
      });
      if (data.status === "sent") {
        toast.success(`Email sent to ${data.sent} recipient${data.sent !== 1 ? "s" : ""}`);
      } else {
        toast.warning(`Sent to ${data.sent}/${data.total} recipients`);
      }
      setEmailComposeOpen(false);
      clearSelection();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleImpersonateFromLog = async (userId, email) => {
    setImpersonatingId(userId);
    try {
      const { data } = await api.post(`/admin/users/${userId}/impersonate`);
      const url = `${window.location.origin}/impersonate?token=${encodeURIComponent(data.token)}`;
      window.open(url, "_blank", "noopener");
      toast.success(`Opened session for ${email || "user"} in new tab`);
    } catch {
      toast.error("Failed to start impersonation");
    } finally {
      setImpersonatingId(null);
    }
  };

  const hasActiveFilters = searchQuery || datePreset || dateFrom || dateTo;

  const clearFilters = () => {
    setSearchInput(""); setSearchQuery("");
    setDatePreset(""); setDateFrom(""); setDateTo("");
    setEntityFilter(""); setLifecycleFilter("");
  };

  const handleDatePreset = (key) => {
    setDatePreset(key);
    if (key === "custom") return;
    const { from, to } = getDatePreset(key);
    setDateFrom(from); setDateTo(to);
  };

  // ── Live search with debounce ──
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== searchQuery) {
        setSearchQuery(trimmed);
        setLifecycleSkip(0);
        setActionCursor(null);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Initialize default time window (15m) on mount ──
  useEffect(() => {
    if (datePreset === "15m" && !dateFrom && !dateTo) {
      const { from, to } = getDatePreset("15m");
      setDateFrom(from);
      setDateTo(to);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 5.3 — Deep-link resolution from support-note URLs.
  // On mount only: if ?event_id=… is present, prefill search + mark it for
  // scroll/highlight once results land. Also widen the time window because
  // deep-linked events are usually older than the default 15 min.
  useEffect(() => {
    const eid = searchParams.get("event_id");
    if (eid) {
      setSearchInput(eid);
      setHighlightEventId(eid);
      setDatePreset("");
      setDateFrom("");
      setDateTo("");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fillSearch = (term) => {
    if (!term) return;
    setSearchInput(term);
  };

  // ── Actions fetch ──
  const fetchActions = useCallback(async (append = false, existingCursor = null) => {
    append ? null : setActionLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (entityFilter) params.set("entity_type", entityFilter);
      if (searchQuery) params.set("search", searchQuery);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (append && existingCursor) params.set("cursor", existingCursor);
      const { data } = await api.get(`/admin/activity-logs?${params}`);
      const fetched = data.logs || [];
      if (append) setActionLogs((prev) => [...prev, ...fetched]);
      else setActionLogs(fetched);
      setActionHasMore(data.has_more);
      setActionCursor(data.next_cursor);
    } catch {
      toast.error("Failed to load activity logs");
    } finally {
      setActionLoading(false);
    }
  }, [entityFilter, searchQuery, dateFrom, dateTo]);

  // ── Lifecycle fetch ──
  const fetchLifecycle = useCallback(async (skip = 0) => {
    setLifecycleLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("schema", "v2");
      params.set("limit", "50");
      params.set("skip", String(skip));
      params.set("sort", sortOrder);
      if (lifecycleFilter) {
        const filterDef = LIFECYCLE_FILTERS.find(f => f.key === lifecycleFilter);
        if (filterDef?.isStatus && lifecycleFilter === "__blocked__") {
          params.set("status", "blocked,failed,denied");
        } else if (filterDef?.isCategory) {
          params.set("category", lifecycleFilter);
        } else if (filterDef?.isCanonical) {
          // Phase 4: canonical bucket — backend expands to all mapped legacy aliases.
          params.set("canonical_event_type", lifecycleFilter);
        } else {
          params.set("event_type", lifecycleFilter);
        }
      }
      if (searchQuery) params.set("search", searchQuery);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (hideTestData) params.set("exclude_em", "1");
      const { data } = await api.get(`/admin/audit-logs?${params}`);
      setLifecycleLogs(data.logs || []);
      setLifecycleTotal(data.total || 0);
      setLifecycleSkip(skip);
    } catch {
      toast.error("Failed to load lifecycle events");
    } finally {
      setLifecycleLoading(false);
    }
  }, [lifecycleFilter, searchQuery, dateFrom, dateTo, sortOrder, hideTestData]);

  useEffect(() => { if (tab === "actions") fetchActions(false); }, [fetchActions, tab]);
  useEffect(() => { if (tab === "lifecycle") fetchLifecycle(0); }, [fetchLifecycle, tab]);

  // ── Auto-refresh polling (admin-controlled rate, persisted in localStorage) ──
  // Defaults: ON, 30s, clamp at 3s minimum. `loadingRef` prevents overlapping fetches.
  const REFRESH_RATE_MIN = 3;
  const REFRESH_RATE_DEFAULT = 30;
  const LS_AUTO_KEY = "activityLog.autoRefresh";
  const LS_RATE_KEY = "activityLog.refreshRateSeconds";

  const readAutoRefreshLS = () => {
    try {
      const v = localStorage.getItem(LS_AUTO_KEY);
      if (v === null) return true;
      return v === "true";
    } catch { return true; }
  };
  const readRateLS = () => {
    try {
      const raw = parseInt(localStorage.getItem(LS_RATE_KEY) || "", 10);
      if (!Number.isFinite(raw) || raw < REFRESH_RATE_MIN) return REFRESH_RATE_DEFAULT;
      return raw;
    } catch { return REFRESH_RATE_DEFAULT; }
  };

  const [autoRefresh, setAutoRefresh] = useState(readAutoRefreshLS);
  const [refreshRate, setRefreshRate] = useState(readRateLS); // seconds, clamped
  const [rateInputValue, setRateInputValue] = useState(() => String(readRateLS())); // raw text in input
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const loadingRef = useRef(false);

  // Persist preferences
  useEffect(() => {
    try { localStorage.setItem(LS_AUTO_KEY, String(autoRefresh)); } catch {}
  }, [autoRefresh]);
  useEffect(() => {
    try { localStorage.setItem(LS_RATE_KEY, String(refreshRate)); } catch {}
  }, [refreshRate]);

  // Refresh action — single source of truth for both manual + interval ticks.
  const doRefresh = useCallback(async () => {
    if (loadingRef.current) return;
    if (emailComposeOpen || userDetail) return;
    loadingRef.current = true;
    try {
      if (tab === "lifecycle" && lifecycleSkip === 0) {
        await fetchLifecycle(0);
      } else if (tab === "actions") {
        await fetchActions(false);
      } else {
        return; // viewing a non-first lifecycle page → skip silently
      }
      setLastRefreshedAt(new Date());
    } finally {
      loadingRef.current = false;
    }
  }, [tab, lifecycleSkip, emailComposeOpen, userDetail, fetchLifecycle, fetchActions]);

  const handleManualRefresh = useCallback(async () => {
    setManualRefreshing(true);
    try { await doRefresh(); }
    finally { setManualRefreshing(false); }
  }, [doRefresh]);

  // Rate input — clamp to >= REFRESH_RATE_MIN on commit (blur / Enter).
  const commitRateInput = () => {
    const parsed = parseInt(rateInputValue, 10);
    const clamped = Number.isFinite(parsed) && parsed >= REFRESH_RATE_MIN
      ? parsed
      : REFRESH_RATE_MIN;
    setRefreshRate(clamped);
    setRateInputValue(String(clamped));
  };

  // Polling effect — pauses when auto-refresh OFF or tab hidden, resumes on visibility.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    let intervalId = null;

    const startInterval = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(() => {
        if (document.visibilityState !== "visible") return;
        doRefresh();
      }, Math.max(REFRESH_RATE_MIN, refreshRate) * 1000);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Resume: kick an immediate refresh and restart the interval.
        doRefresh();
        startInterval();
      } else if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    if (document.visibilityState === "visible") startInterval();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [autoRefresh, refreshRate, doRefresh]);

  // Phase 5.3 — Scroll/highlight the row matching highlightEventId once it's rendered.
  // Fires whenever logs change (after fetch) and the target event_id is set.
  useEffect(() => {
    if (!highlightEventId) return;
    // Wait one frame so DOM paints the new rows.
    const rafId = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-event-id="${highlightEventId}"]`);
      if (!el) return;
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch {}
      el.classList.add("activity-row-deeplink-highlight");
      // Clear target once consumed so manual edits to the search field don't
      // keep re-highlighting. The CSS class handles its own fade-out.
      setTimeout(() => setHighlightEventId(""), 3200);
    });
    return () => cancelAnimationFrame(rafId);
  }, [highlightEventId, lifecycleLogs, actionLogs]);

  return (
  <>
    <ScrollArea className="h-full">
      <div className="p-6 space-y-5 max-w-[1100px]" data-testid="admin-activity-page">
        {/* Header */}
        <div>
          <h2 className="text-xl font-semibold text-[#111111] tracking-tight">Activity & Lifecycle</h2>
          <p className="text-sm text-[#888888] mt-0.5">Search, filter, and inspect all tracked events</p>
        </div>

        {/* Search Bar */}
        <div className="flex gap-2" data-testid="log-search-bar">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888888]" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by email, user ID, alias, event type..."
              className="h-9 pl-9 pr-8 text-sm"
              data-testid="log-search-input"
            />
            {searchInput && (
              <button
                onClick={() => { setSearchInput(""); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#888888] hover:text-[#555555]"
                data-testid="log-search-clear"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {hasActiveFilters && (
            <Button size="sm" variant="outline" onClick={clearFilters} className="text-xs h-9 text-[#888888]" data-testid="log-clear-filters">
              <X size={13} className="mr-1" /> Reset all
            </Button>
          )}
        </div>

        {/* Date Presets */}
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="date-filter-bar">
          <Calendar size={13} className="text-[#888888] mr-1" />
          {DATE_PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => handleDatePreset(p.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                datePreset === p.key
                  ? "bg-zinc-900 text-white"
                  : "bg-[#F5F5F5] text-[#888888] hover:bg-[#EBEBEB] hover:text-[#555555]"
              }`}
              data-testid={`date-filter-${p.key || "all"}`}
            >{p.label}</button>
          ))}
          {datePreset === "custom" && (
            <div className="flex items-center gap-1.5 ml-2">
              <input
                type="date"
                value={dateFrom ? dateFrom.slice(0, 10) : ""}
                onChange={(e) => setDateFrom(e.target.value ? new Date(e.target.value + "T00:00:00Z").toISOString() : "")}
                className="h-8 px-2 text-xs border border-[#E5E5E5] rounded-md bg-white"
                data-testid="date-from-input"
              />
              <span className="text-xs text-[#888888]">to</span>
              <input
                type="date"
                value={dateTo ? dateTo.slice(0, 10) : ""}
                onChange={(e) => setDateTo(e.target.value ? new Date(e.target.value + "T23:59:59Z").toISOString() : "")}
                className="h-8 px-2 text-xs border border-[#E5E5E5] rounded-md bg-white"
                data-testid="date-to-input"
              />
            </div>
          )}
        </div>

        {/* Active filters summary */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="active-filters">
            {searchQuery && <Badge variant="outline" className="text-[10px] gap-1">Search: {searchQuery}</Badge>}
            {datePreset && datePreset !== "custom" && <Badge variant="outline" className="text-[10px] gap-1">{DATE_PRESETS.find(p => p.key === datePreset)?.label}</Badge>}
            {datePreset === "custom" && dateFrom && <Badge variant="outline" className="text-[10px] gap-1">From: {dateFrom.slice(0, 10)}</Badge>}
            {datePreset === "custom" && dateTo && <Badge variant="outline" className="text-[10px] gap-1">To: {dateTo.slice(0, 10)}</Badge>}
          </div>
        )}

        {/* Tab switch + auto-refresh control */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-1 p-1 bg-[#F5F5F5] rounded-lg w-fit" data-testid="activity-tabs">
            <button
              onClick={() => setTab("lifecycle")}
              className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "lifecycle" ? "bg-white text-[#111111] shadow-sm" : "text-[#888888] hover:text-[#555555]"}`}
              data-testid="tab-lifecycle"
            >Lifecycle</button>
            <button
              onClick={() => setTab("actions")}
              className={`px-4 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "actions" ? "bg-white text-[#111111] shadow-sm" : "text-[#888888] hover:text-[#555555]"}`}
              data-testid="tab-actions"
            >Actions</button>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-[#888888] flex-wrap" data-testid="activity-refresh-controls">
            {/* Auto-refresh toggle */}
            <button
              onClick={() => setAutoRefresh(v => !v)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors ${autoRefresh ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-[#F5F5F5] text-[#555555] hover:bg-[#EBEBEB]"}`}
              data-testid="activity-autorefresh-toggle"
              title={autoRefresh ? `Auto-refresh every ${refreshRate}s — click to pause` : "Auto-refresh paused — click to enable"}
              aria-pressed={autoRefresh}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? "bg-emerald-500 animate-pulse" : "bg-[#888888]"}`} />
              Auto-refresh: {autoRefresh ? "ON" : "OFF"}
            </button>

            {/* Refresh rate input */}
            <label className="flex items-center gap-1" title="Minimum 3 seconds">
              <span className="text-[11px]">Refresh rate</span>
              <input
                type="number"
                min={REFRESH_RATE_MIN}
                step={1}
                value={rateInputValue}
                onChange={(e) => setRateInputValue(e.target.value)}
                onBlur={commitRateInput}
                onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
                disabled={!autoRefresh}
                className="h-7 w-14 px-1.5 text-xs text-center border border-[#E5E5E5] rounded-md bg-white tabular-nums disabled:bg-[#F5F5F5] disabled:text-[#AAAAAA]"
                data-testid="activity-refresh-rate-input"
                aria-label="Auto-refresh rate in seconds"
              />
              <span className="text-[11px]">s</span>
              <span className="text-[10px] text-[#AAAAAA] ml-0.5" data-testid="activity-refresh-rate-hint">Min: 3s</span>
            </label>

            {/* Manual refresh */}
            <button
              onClick={handleManualRefresh}
              disabled={manualRefreshing}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#F5F5F5] text-[#555555] hover:bg-[#EBEBEB] disabled:opacity-60"
              data-testid="activity-refresh-now"
              title="Refresh the activity log now"
            >
              <RefreshCw size={11} className={manualRefreshing ? "animate-spin" : ""} />
              Refresh now
            </button>

            {lastRefreshedAt && (
              <span className="tabular-nums" data-testid="activity-last-refresh">
                updated {new Date(lastRefreshedAt).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </span>
            )}
          </div>
        </div>

        {/* ── LIFECYCLE TAB ── */}
        {tab === "lifecycle" && (
          <>
            <div className="flex items-center gap-1.5 flex-wrap justify-between" data-testid="lifecycle-filters">
              <div className="flex items-center gap-1.5 flex-wrap">
                {LIFECYCLE_FILTERS.map((f) => {
                  const isActive = lifecycleFilter === f.key;
                  const isDanger = f.tone === "danger";
                  const activeClass = isDanger ? "bg-red-600 text-white" : "bg-zinc-900 text-white";
                  const inactiveClass = isDanger
                    ? "bg-red-50 text-red-600 hover:bg-red-100"
                    : "bg-[#F5F5F5] text-[#888888] hover:bg-[#EBEBEB] hover:text-[#555555]";
                  return (
                    <button
                      key={f.key}
                      onClick={() => { setLifecycleFilter(f.key); setLifecycleSkip(0); }}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${isActive ? activeClass : inactiveClass}`}
                      data-testid={`lifecycle-filter-${f.key === "__blocked__" ? "blocked" : (f.key || "all")}`}
                    >{f.label}{isDanger ? " \u26A0\uFE0E" : ""}</button>
                  );
                })}
              </div>
              <button
                onClick={() => setSortOrder(v => v === "desc" ? "asc" : "desc")}
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-[#F5F5F5] text-[#555555] hover:bg-[#EBEBEB] transition-colors flex items-center gap-1.5"
                data-testid="lifecycle-sort-toggle"
                title={sortOrder === "desc" ? "Newest first (click for oldest first)" : "Oldest first (click for newest first)"}
              >
                {sortOrder === "desc" ? "\u2193 Newest" : "\u2191 Oldest"}
              </button>
            </div>

            {lifecycleLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-5 h-5 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : lifecycleLogs.length === 0 ? (
              <div className="text-center py-16 text-[#888888]" data-testid="lifecycle-empty">
                <UserCheck size={28} className="mx-auto mb-2 text-[#555555]" />
                <p className="text-sm">No events match your filters</p>
                {hasActiveFilters && <p className="text-xs text-[#555555] mt-1">Try adjusting your search or date range</p>}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-[#888888]" data-testid="lifecycle-count">{lifecycleTotal} total events (showing {lifecycleSkip + 1}&ndash;{Math.min(lifecycleSkip + 50, lifecycleTotal)})</p>
                  {selectedEvents.length > 0 && (
                    <button
                      onClick={clearSelection}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium flex items-center gap-1"
                      data-testid="deselect-all"
                    >
                      <X size={12} /> Deselect all ({selectedEvents.length})
                    </button>
                  )}
                </div>
                <div className="border border-[#E5E5E5] rounded-lg overflow-x-auto overflow-y-hidden bg-white" data-testid="lifecycle-list" style={{ WebkitOverflowScrolling: "touch" }}>
                  {/* Inner wrapper holds the table at its natural min width so
                      narrow viewports (mobile/tablet) get a scrollbar instead
                      of cropping unreachable columns. Desktop is unchanged
                      because at ≥640px content fits within the wrapper. */}
                  <div className="min-w-[640px]">
                  {/* Table header */}
                  <div className="grid grid-cols-[28px_minmax(130px,1fr)_100px_minmax(140px,1.2fr)_70px_130px] gap-2 px-4 py-2.5 bg-[#F5F5F5] border-b border-[#E5E5E5] text-[10px] font-mono uppercase tracking-widest text-[#888888]">
                    <span></span>
                    <span>Actor</span>
                    <span>Action</span>
                    <span>Target</span>
                    <span>Status</span>
                    <span>Time</span>
                  </div>
                  {/* Table body */}
                  <div className="divide-y divide-[#E5E5E5]">
                    {lifecycleLogs.map((log, i) => {
                      const cat = log.category || "system";
                      const CatIcon = CATEGORY_ICONS[cat] || Activity;
                      const catColors = CATEGORY_COLORS[cat] || "text-[#888888] bg-[#F5F5F5]";
                      const [catIconColor, catIconBg] = catColors.split(" ");
                      const actionColor = ACTION_COLORS[log.action] || "text-[#555555]";
                      const statusStyle = STATUS_STYLES[log.status] || "bg-[#F5F5F5] text-[#888888] border-[#E5E5E5]";
                      const actor = log.actor || {};
                      const target = log.target || {};

                      // Transform old email_received events: swap actor/target for recipient-first display
                      let displayActor = actor;
                      let displayAction = humanActionLabel(log.action || "");
                      let displayTarget = target;
                      const meta = log.metadata || {};

                      if ((log.action === "email_received" || log.action === "received_email") && actor.type === "external") {
                        // Old format: actor=sender, target=email — flip to recipient-first
                        const aliasId = meta.alias_id || "";
                        const userId = meta.user_id || "";
                        const userName = meta.user_name || "";
                        if (userId) {
                          displayActor = { type: "user", id: userId, label: userName ? `${userName} (${userId.slice(0, 8)})` : userId.slice(0, 12), label_short: userName ? `${userName} (${userId.slice(0, 8)})` : userId.slice(0, 12) };
                        } else if (aliasId) {
                          const aliasAddr = meta.alias_address || "";
                          displayActor = { type: "alias", id: aliasId, label: aliasAddr || `alias (${aliasId.slice(0, 8)})`, label_short: aliasAddr || `alias (${aliasId.slice(0, 8)})` };
                        } else {
                          displayActor = { type: "alias", id: "", label: "recipient", label_short: "recipient" };
                        }
                        displayAction = "received email";
                        displayTarget = { type: "sender", id: "", label: actor.label || "", label_short: actor.label_short || actor.label || "" };
                      } else if (log.action === "received_email" && actor.type === "user") {
                        // New format: already recipient-first
                        displayAction = "received email";
                      }

                      // Determine the primary clickable action for the actor cell
                      const actorIsUser = displayActor.type === "user" && displayActor.id;
                      const actorIsAlias = displayActor.type === "alias" && displayActor.id;
                      // For non-email events, check if there's a user_id in metadata or actor
                      const fallbackUserId = !actorIsUser && !actorIsAlias ? (meta.user_id || actor.id || "") : "";
                      const isClickable = actorIsUser || actorIsAlias || (actor.type === "user" && actor.id);

                      const handleActorClick = () => {
                        if (actorIsUser) {
                          openUserDetail(displayActor.id);
                        } else if (actorIsAlias) {
                          openAliasInbox(displayActor.id);
                        } else if (actor.type === "user" && actor.id) {
                          openUserDetail(actor.id);
                        }
                      };

                      const eventId = log.event_type ? `${log.event_type}_${log.timestamp}_${i}` : `${log.action}_${log.timestamp}_${i}`;
                      const isChecked = selectedEvents.some(e => e.event_id === eventId);
                      const isDeepLinkTarget = !!(log.event_id && highlightEventId && log.event_id === highlightEventId);

                      return (
                        <div
                          key={i}
                          data-event-id={log.event_id || ""}
                          className={`grid grid-cols-[28px_minmax(130px,1fr)_100px_minmax(140px,1.2fr)_70px_130px] gap-2 px-4 py-2.5 hover:bg-[#F7F7F7] items-center ${isChecked ? "bg-blue-50/50" : ""} ${isDeepLinkTarget ? "activity-row-deeplink-highlight" : ""}`}
                          data-testid={`lifecycle-event-${i}`}
                        >
                          {/* Checkbox */}
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleEventSelection(log, i); }}
                            className={`w-4 h-4 rounded border flex items-center justify-center transition-colors shrink-0 ${
                              isChecked ? "bg-blue-600 border-blue-600" : "border-zinc-300 hover:border-zinc-400"
                            }`}
                            data-testid={`event-checkbox-${i}`}
                          >
                            {isChecked && <Check size={10} className="text-white" strokeWidth={3} />}
                          </button>
                          {/* Actor */}
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-6 h-6 rounded-full ${catIconBg} flex items-center justify-center shrink-0`}>
                              <CatIcon size={12} className={catIconColor} />
                            </div>
                            {isClickable ? (
                              <button
                                onClick={handleActorClick}
                                className="text-xs font-mono text-blue-600 hover:text-blue-800 hover:underline truncate text-left"
                                title={displayActor.label || actor.label || ""}
                                data-testid={`event-actor-link-${i}`}
                              >
                                <HighlightText text={displayActor.label || displayActor.label_short || actor.label || actor.label_short || "system"} query={searchQuery} />
                              </button>
                            ) : (
                              <span className="text-xs font-mono text-[#555555] truncate" title={displayActor.label || actor.label || ""}>
                                <HighlightText text={displayActor.label || displayActor.label_short || actor.label || actor.label_short || "system"} query={searchQuery} />
                              </span>
                            )}
                          </div>
                          {/* Action — click to search by action */}
                          <button
                            onClick={() => fillSearch(log.action || "")}
                            className={`flex flex-col gap-0 text-xs font-medium ${actionColor} hover:underline text-left`}
                            title={log.canonical_event_type ? `canonical: ${log.canonical_event_type} · raw action: ${log.action || ""}` : `Search: ${displayAction}`}
                            data-testid={`event-action-${i}`}
                          >
                            <span className="truncate">
                              <HighlightText
                                text={log.canonical_event_type ? (CANONICAL_HUMAN_LABELS[log.canonical_event_type] || displayAction) : displayAction}
                                query={searchQuery}
                              />
                            </span>
                            {log.canonical_event_type && log.canonical_event_type !== (log.action || "") && (
                              <span className="text-[9px] font-mono text-[#AAAAAA] truncate" data-testid={`event-canonical-${i}`}>
                                {log.canonical_event_type}
                              </span>
                            )}
                          </button>
                          {/* Target — click to search by target value */}
                          {(displayTarget.label_short || displayTarget.label) ? (
                            <button
                              onClick={() => {
                                // Extract local part for email, or use full label
                                const raw = displayTarget.label || "";
                                const term = raw.includes("@") ? raw.split("@")[0] : raw;
                                fillSearch(term);
                              }}
                              className="text-xs font-mono text-[#555555] truncate hover:text-blue-600 hover:underline text-left"
                              title={`Search: ${displayTarget.label || ""}`}
                              data-testid={`event-target-${i}`}
                            >
                              <HighlightText text={displayTarget.label || displayTarget.label_short} query={searchQuery} />
                            </button>
                          ) : (
                            <span className="text-xs font-mono text-[#888888]" data-testid={`event-target-${i}`}>—</span>
                          )}
                          {/* Status + reason for blocked/failed events */}
                          <span className="flex flex-col gap-0.5">
                            <Badge variant="outline" className={`text-[9px] px-1.5 py-0 font-mono ${statusStyle} w-fit`} data-testid={`event-status-${i}`}>
                              {log.status || "ok"}
                            </Badge>
                            {(log.status === "blocked" || log.status === "failed" || log.status === "denied") && meta.reason && (
                              <span className="text-[9px] text-red-600 font-mono" title={meta.reason} data-testid={`event-reason-${i}`}>
                                {String(meta.reason).replace(/_/g, " ")}
                              </span>
                            )}
                          </span>
                          {/* Time */}
                          <span className="text-[11px] text-[#888888] tabular-nums">{formatTime(log.timestamp)}</span>
                        </div>
                      );
                    })}
                  </div>
                  </div>
                </div>
                {lifecycleTotal > 50 && (
                  <div className="flex items-center justify-center gap-3 py-2">
                    <Button variant="outline" size="sm" disabled={lifecycleSkip === 0} onClick={() => fetchLifecycle(Math.max(0, lifecycleSkip - 50))} className="text-xs" data-testid="lifecycle-prev">
                      <ChevronLeft size={13} className="mr-1" /> Previous
                    </Button>
                    <span className="text-xs text-[#888888] tabular-nums">Page {Math.floor(lifecycleSkip / 50) + 1} of {Math.ceil(lifecycleTotal / 50)}</span>
                    <Button variant="outline" size="sm" disabled={lifecycleSkip + 50 >= lifecycleTotal} onClick={() => fetchLifecycle(lifecycleSkip + 50)} className="text-xs" data-testid="lifecycle-next">
                      Next <ChevronRight size={13} className="ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ── ACTIONS TAB ── */}
        {tab === "actions" && (
          <>
            <div className="flex items-center gap-1.5" data-testid="admin-activity-filters">
              {ENTITY_FILTERS.map((f) => (
                <button
                  key={f.key}
                  onClick={() => setEntityFilter(f.key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${entityFilter === f.key ? "bg-zinc-900 text-white" : "bg-[#F5F5F5] text-[#888888] hover:bg-[#EBEBEB] hover:text-[#555555]"}`}
                  data-testid={`admin-activity-filter-${f.key || "all"}`}
                >{f.label}</button>
              ))}
            </div>

            {actionLoading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-5 h-5 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : actionLogs.length === 0 ? (
              <div className="text-center py-16 text-[#888888]" data-testid="admin-activity-empty">
                <Activity size={28} className="mx-auto mb-2 text-[#555555]" />
                <p className="text-sm">No events match your filters</p>
                {hasActiveFilters && <p className="text-xs text-[#555555] mt-1">Try adjusting your search or date range</p>}
              </div>
            ) : (
              <div className="border border-[#E5E5E5] rounded-lg overflow-x-auto overflow-y-hidden bg-white" data-testid="admin-activity-list" style={{ WebkitOverflowScrolling: "touch" }}>
                <div className="min-w-[640px]">
                {/* Table header */}
                <div className="grid grid-cols-[28px_minmax(130px,1fr)_100px_minmax(140px,1.2fr)_70px_130px] gap-2 px-4 py-2.5 bg-[#F5F5F5] border-b border-[#E5E5E5] text-[10px] font-mono uppercase tracking-widest text-[#888888]">
                  <span></span>
                  <span>Actor</span>
                  <span>Action</span>
                  <span>Target</span>
                  <span>Type</span>
                  <span>Time</span>
                </div>
                {actionLogs.map((log, i) => {
                  const Icon = EVENT_ICONS[log.event_type] || Activity;
                  const colors = EVENT_COLORS[log.event_type] || "text-[#888888] bg-[#F5F5F5]";
                  const [iconColor, iconBg] = colors.split(" ");
                  const actionColor = ACTION_COLORS[(log.event_type || "").split(".").pop()] || ACTION_COLORS[(log.event_type || "").replace(/[._]/g, "_")] || "text-[#555555]";

                  // Extract actor/action/target from activity_logs format
                  const md = log.metadata || {};
                  const actorEmail = log.actor_email || md.email || md.admin_email || "";
                  const actorId = log.actor_id || "";
                  const eventAction = humanActionLabel(log.event_type || "");
                  const targetStr = md.target_email || md.username || md.to_email || md.query || md.name || "";
                  const isActorUser = !!actorId && actorEmail;

                  return (
                    <div key={log.log_id || i} className="grid grid-cols-[28px_minmax(130px,1fr)_100px_minmax(140px,1.2fr)_70px_130px] gap-2 px-4 py-2.5 hover:bg-[#F7F7F7] items-center border-b border-[#E5E5E5] last:border-b-0" data-testid={`admin-activity-event-${log.log_id || i}`}>
                      {/* Icon */}
                      <div className={`w-6 h-6 rounded-full ${iconBg} flex items-center justify-center shrink-0`}>
                        <Icon size={12} className={iconColor} />
                      </div>
                      {/* Actor */}
                      {isActorUser ? (
                        <button
                          onClick={() => openUserDetail(actorId)}
                          className="text-xs font-mono text-blue-600 hover:text-blue-800 hover:underline truncate text-left"
                          title={actorEmail}
                          data-testid={`action-actor-link-${i}`}
                        >
                          <HighlightText text={actorEmail} query={searchQuery} />
                        </button>
                      ) : (
                        <span className="text-xs font-mono text-[#555555] truncate" title={actorEmail}>
                          <HighlightText text={actorEmail || "system"} query={searchQuery} />
                        </span>
                      )}
                      {/* Action */}
                      <button
                        onClick={() => fillSearch(log.event_type || "")}
                        className={`text-xs font-medium truncate ${actionColor} hover:underline text-left`}
                        title={`Search: ${eventAction}`}
                        data-testid={`action-action-${i}`}
                      >
                        <HighlightText text={eventAction} query={searchQuery} />
                      </button>
                      {/* Target */}
                      {targetStr ? (
                        <button
                          onClick={() => {
                            const term = targetStr.includes("@") ? targetStr.split("@")[0] : targetStr;
                            fillSearch(term);
                          }}
                          className="text-xs font-mono text-[#555555] truncate hover:text-blue-600 hover:underline text-left"
                          title={targetStr}
                          data-testid={`action-target-${i}`}
                        >
                          <HighlightText text={targetStr} query={searchQuery} />
                        </button>
                      ) : (
                        <span className="text-xs font-mono text-[#888888]" data-testid={`action-target-${i}`}>—</span>
                      )}
                      {/* Type */}
                      <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-[#888888] border-[#E5E5E5] font-normal w-fit">{log.entity_type || "—"}</Badge>
                      {/* Time */}
                      <span className="text-[11px] text-[#888888] tabular-nums truncate">{formatTime(log.created_at)}</span>
                    </div>
                  );
                })}
                </div>
              </div>
            )}

            {actionHasMore && (
              <div className="flex justify-center py-2">
                <Button variant="outline" size="sm" onClick={() => actionCursor && fetchActions(true, actionCursor)} className="text-xs" data-testid="admin-activity-load-more">
                  <ChevronDown size={13} className="mr-1.5" /> Load more
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </ScrollArea>

    {/* User Detail Dialog */}
    <Dialog open={!!userDetail} onOpenChange={() => setUserDetail(null)}>
      <DialogContent className="sm:max-w-[520px] p-0" data-testid="activity-user-detail-modal">
        {userDetail && (
          <div className="max-h-[85vh] overflow-y-auto p-6">
            <DialogHeader>
              <DialogTitle className="text-lg flex items-center gap-2">
                {userDetail.name || userDetail.email}
                <Badge variant="outline" className="text-[10px] font-mono capitalize">{userDetail.role || "user"}</Badge>
              </DialogTitle>
              <DialogDescription className="text-xs font-mono text-[#888888]">
                {userDetail.email}
                {userDetail.user_id && <span className="ml-2 text-[#AAAAAA]">ID: {userDetail.user_id.slice(0, 10)}</span>}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-3">
              {/* Activity Snapshot */}
              <div className="p-3 bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg space-y-1.5">
                <p className="text-[10px] font-mono uppercase tracking-widest text-[#888888] mb-2">Activity Snapshot</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#888888] flex items-center gap-1.5"><Clock size={11} />Last Login</span>
                  <span className="text-[#555555] font-medium">{fmtDetailDate(userDetail.last_login)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#888888] flex items-center gap-1.5"><Activity size={11} />Last Action</span>
                  <span className="text-[#555555] font-medium">{fmtDetailDate(userDetail.last_action)}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#888888] flex items-center gap-1.5"><Clock size={11} />Last Seen</span>
                  <span className="text-[#555555] font-medium">{fmtDetailDate(userDetail.last_seen_at)}</span>
                </div>
              </div>

              {/* Usage Metrics */}
              {userDetail.usage && (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { icon: AtSign, label: "Aliases", value: userDetail.usage.aliases ?? 0 },
                    { icon: Mail, label: "Received", value: userDetail.usage.emails_received ?? 0 },
                    { icon: Mail, label: "Sent", value: userDetail.usage.emails_sent ?? 0 },
                    { icon: Forward, label: "Forwarded", value: userDetail.usage.emails_forwarded ?? 0 },
                    { icon: Ban, label: "Blocked", value: userDetail.usage.emails_blocked ?? 0 },
                    { icon: Shield, label: "Rules", value: userDetail.usage.active_rules ?? 0 },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="p-2.5 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] text-center">
                      <Icon size={14} className="mx-auto mb-1 text-[#888888]" />
                      <p className="text-lg font-semibold text-[#111111] tabular-nums">{value}</p>
                      <p className="text-[9px] font-mono uppercase text-[#888888]">{label}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Meta */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs py-1">
                  <span className="text-[#888888] flex items-center gap-1.5"><CreditCard size={11} />Plan</span>
                  <Badge variant="outline" className="text-[10px] font-mono capitalize">{userDetail.plan || "free"}</Badge>
                </div>
                <div className="flex items-center justify-between text-xs py-1">
                  <span className="text-[#888888] flex items-center gap-1.5"><Clock size={11} />Joined</span>
                  <span className="text-[#555555] font-medium">{fmtDetailDate(userDetail.created_at)}</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-5 space-y-2">
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => {
                    setUserDetail(null);
                    try { localStorage.setItem("inbox_impersonate_tenant", userDetail.tenant_id); } catch {}
                    navigate("/inbox");
                  }}
                  data-testid="user-detail-open-inbox"
                >
                  <Inbox size={12} className="mr-1.5" /> Open Inbox
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1 text-xs"
                  onClick={() => { setUserDetail(null); navigate("/housekeeping/users"); }}
                  data-testid="user-detail-view-aliases"
                >
                  <AtSign size={12} className="mr-1.5" /> View in Users
                </Button>
              </div>
              <Button size="sm" variant="ghost" className="w-full text-xs text-[#888888] h-8" onClick={() => setUserDetail(null)} data-testid="user-detail-close">
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Loading overlay for user detail */}
    {userDetailLoading && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/10">
        <div className="bg-white rounded-lg shadow-lg p-6 flex items-center gap-3">
          <Loader2 size={18} className="animate-spin text-blue-600" />
          <span className="text-sm text-[#555555]">Loading user details...</span>
        </div>
      </div>
    )}

    {/* ── Floating Selection Basket ── */}
    {selectedEvents.length > 0 && (
      <div className="fixed bottom-6 right-6 w-80 bg-white border border-[#E5E5E5] rounded-xl shadow-lg z-40 overflow-hidden" data-testid="event-basket">
        <div className="px-4 py-3 border-b border-[#E5E5E5] bg-[#F9F9F9]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckSquare size={14} className="text-blue-600" />
              <span className="text-sm font-medium text-[#111111]">Selected events ({selectedEvents.length})</span>
            </div>
            <button onClick={clearSelection} className="text-[#888888] hover:text-[#555555]" data-testid="basket-clear">
              <X size={14} />
            </button>
          </div>
          {selectedUserLabel && (
            <p className="text-[11px] text-[#888888] mt-1 truncate">User: <span className="text-[#555555] font-medium">{selectedUserLabel}</span></p>
          )}
        </div>
        <div className="max-h-40 overflow-y-auto px-4 py-2 space-y-1.5">
          {selectedEvents.map((ev) => (
            <div key={ev.event_id} className="flex items-start gap-2 text-[11px]">
              <span className="text-[#888888] shrink-0 tabular-nums">{ev.timestamp ? new Date(ev.timestamp).toLocaleDateString("de-DE", { day: "2-digit", month: "short" }) : ""}</span>
              <span className="text-[#555555] truncate">{ev.display_text}</span>
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-[#E5E5E5] flex gap-2">
          <Button size="sm" variant="outline" onClick={clearSelection} className="text-xs h-8 px-3 shrink-0" data-testid="basket-clear-btn">
            <X size={12} className="mr-1" /> Clear
          </Button>
          <Button size="sm" variant="outline" onClick={handleQuickCopy} className="flex-1 text-xs h-8" data-testid="basket-copy-btn">
            <Download size={12} className="mr-1" /> Copy
          </Button>
          <Button size="sm" onClick={openEmailCompose} className="flex-1 text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white" data-testid="basket-create-email">
            <Mail size={12} className="mr-1.5" /> Create email
          </Button>
        </div>
      </div>
    )}

    {/* ── Conflict Prompt Dialog ── */}
    <Dialog open={!!showConflictPrompt} onOpenChange={() => setShowConflictPrompt(null)}>
      <DialogContent className="sm:max-w-[400px]" data-testid="conflict-prompt-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Different user selected</DialogTitle>
          <DialogDescription className="text-sm text-[#888888]">
            You already selected events from <span className="font-medium text-[#555555]">{selectedUserLabel}</span>. Only one user per selection is allowed.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" onClick={() => setShowConflictPrompt(null)} className="flex-1 text-xs" data-testid="conflict-cancel">
            Cancel
          </Button>
          <Button onClick={handleConflictReset} className="flex-1 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="conflict-reset">
            Reset & continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* ── Email Compose Dialog ── */}
    <Dialog open={emailComposeOpen} onOpenChange={setEmailComposeOpen}>
      <DialogContent className="sm:max-w-[580px] p-0 max-h-[90vh] overflow-hidden" data-testid="email-compose-dialog">
        <div className="p-6 space-y-4 overflow-y-auto max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-base">Create email from events</DialogTitle>
            <DialogDescription className="text-sm text-[#888888]">
              Compose and send an email based on {selectedEvents.length} selected event{selectedEvents.length !== 1 ? "s" : ""}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-[#555555] mb-1 block">To</label>
              <Input
                value={composeRecipient}
                onChange={(e) => setComposeRecipient(e.target.value)}
                placeholder="user@domain.com; support@domain.com"
                className="h-9 text-sm font-mono"
                data-testid="compose-to"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-[10px] text-[#888888]">Separate multiple recipients with semicolons</p>
                {selectedUserLabel && selectedUserEmail && selectedUserLabel !== selectedUserEmail && (
                  <p className="text-[10px] text-[#888888]" data-testid="compose-account-hint">Account: <span className="font-medium text-[#555555]">{selectedUserLabel}</span></p>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#555555] mb-1 block">Subject</label>
              <Input
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="Email subject"
                className="h-9 text-sm"
                data-testid="compose-subject"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-[#555555] mb-1 block">Body</label>
              <Textarea
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                rows={8}
                className="text-sm resize-none"
                data-testid="compose-body"
              />
            </div>

            {/* Attachments */}
            <div>
              <label className="text-xs font-medium text-[#555555] mb-1.5 block">Attachments</label>
              {composeAttachments.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {composeAttachments.map((att, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-2.5 py-1.5 rounded bg-[#F5F5F5] border border-[#E5E5E5]">
                      <Paperclip size={12} className="text-[#888888] shrink-0" />
                      <span className="text-xs text-[#555555] truncate flex-1">{att.filename}</span>
                      <span className="text-[10px] text-[#888888] tabular-nums shrink-0">{(att.size / 1024).toFixed(0)} KB</span>
                      <button
                        onClick={() => setComposeAttachments(prev => prev.filter((_, i) => i !== idx))}
                        className="text-[#888888] hover:text-red-500"
                        data-testid={`compose-remove-attachment-${idx}`}
                      >
                        <XCircle size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 cursor-pointer font-medium">
                <Paperclip size={12} />
                Add file
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="compose-file-input"
                />
              </label>
              <span className="text-[10px] text-[#888888] ml-2">Max 5MB per file</span>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setEmailComposeOpen(false)} className="text-xs h-9" data-testid="compose-cancel">
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                const full = `To: ${composeRecipient}\nSubject: ${composeSubject}\n\n${composeBody}`;
                navigator.clipboard.writeText(full).then(() => {
                  toast.success("Email copied to clipboard");
                }).catch(() => toast.error("Copy failed"));
              }}
              className="text-xs h-9"
              data-testid="compose-copy-email"
            >
              <Download size={12} className="mr-1.5" /> Copy email
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !composeRecipient.trim()}
              className="flex-1 text-xs h-9 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="compose-send"
            >
              {sending ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Send size={12} className="mr-1.5" />}
              {sending ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </>
  );
}
